"use client";

import { Utensils, UtensilsCrossed } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

// npm install html5-qrcode
//
// Secured check-in scanner. Scans a ticket QR, sends the value to your
// Express `POST /checkin` endpoint (which verifies the HMAC signature and
// flips `checkedIn` in MongoDB), and shows the result.
//
// Usage:
//   import CheckInScanner from "@/components/CheckInScanner";
//   <CheckInScanner apiBase="https://silvamethod.in:5000" />
//
// `apiBase` is where /checkin lives ("" if same-origin). Camera requires HTTPS.

const REGION_ID = "silva-qr-region";
const GATE_KEY_STORAGE = "silvaGateKey";


export default function ScanTicket() {
  const apiBase = process.env.REACT_APP_SERVER_URI || "";
  const [started, setStarted] = useState(false);
  const [gateKey, setGateKey] = useState("");
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null); // { ok, reason, at, ticket }
  const [camError, setCamError] = useState(false);

  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const onScanRef = useRef(() => { });

  // Load a previously saved gate key (and auto-start if present).
  useEffect(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem(GATE_KEY_STORAGE) : "";
    if (saved) {
      setGateKey(saved);
      setStarted(true);
    }
  }, []);

  const submitCode = useCallback(
    async (code) => {
      if (!code || busyRef.current) return;
      busyRef.current = true;

      // pause live scanning while we show the result
      if (scannerRef.current) {
        try {
          scannerRef.current.pause(true);
        } catch (_) { }
      }

      try {
        const res = await fetch(`${apiBase}tickets/checkin`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-gate-key": localStorage.getItem(GATE_KEY_STORAGE) || "",
          },
          body: JSON.stringify({ code }),
        });
        const data = await res.json();
        setResult(data);
      } catch (_) {
        setResult({ ok: false, reason: "network_error" });
      }
    },
    [apiBase]
  );

  // keep the scan handler current without restarting the camera
  onScanRef.current = (decodedText) => {
    if (!busyRef.current) submitCode(decodedText.trim());
  };

  // Start / stop the camera when `started` flips. Dynamic import keeps it
  // SSR-safe; cleanup waits for start() to settle and only stops when the
  // scanner is actually running (StrictMode-safe).
  useEffect(() => {
    if (!started) return;
    let cancelled = false;

    const setup = (async () => {
      const { Html5Qrcode } = await import("html5-qrcode");
      if (cancelled) return null;
      const inst = new Html5Qrcode(REGION_ID);
      scannerRef.current = inst;
      try {
        await inst.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (text) => onScanRef.current(text),
          () => { } // ignore per-frame decode misses
        );
      } catch (e) {
        if (!cancelled) setCamError(true);
      }
      return inst;
    })();

    return () => {
      cancelled = true;
      scannerRef.current = null;
      setup
        .then((inst) => {
          if (!inst) return;
          let state = null;
          try {
            state = typeof inst.getState === "function" ? inst.getState() : null;
          } catch (_) { }
          // 2 = SCANNING, 3 = PAUSED
          const stopping =
            state === 2 || state === 3 ? inst.stop() : Promise.resolve();
          return stopping.then(() => {
            try {
              inst.clear();
            } catch (_) { }
          });
        })
        .catch(() => { });
    };
  }, [started]);

  const handleStart = () => {
    const key = gateKey.trim();
    if (!key) return;
    localStorage.setItem(GATE_KEY_STORAGE, key);
    setCamError(false);
    setStarted(true);
  };

  const resume = () => {
    busyRef.current = false;
    setResult(null);
    setManual("");
    if (scannerRef.current) {
      try {
        scannerRef.current.resume();
      } catch (_) { }
    }
  };

  const view = describeResult(result);

  return (
    <div
      className={`relative flex min-h-[100dvh] flex-col bg-[#0b1f3a] text-white`}
    >
      <header className="flex items-center justify-between border-b-2 border-[#d4af37] px-4 py-3">
        <span className="font-extrabold tracking-wide">Silva Method — Check-In</span>
        {started && <span className="text-sm font-semibold text-[#d4af37]">gate active</span>}
      </header>

      {/* Setup: enter the gate key once */}
      {!started && (
        <div className="mx-auto w-full max-w-md p-4">
          <p className="mb-2 mt-2 text-center text-sm text-slate-300">
            Enter the gate key to start.
          </p>
          <input
            type="password"
            value={gateKey}
            onChange={(e) => setGateKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStart()}
            placeholder="Gate key"
            autoComplete="off"
            className="w-full rounded-lg border border-[#2b3a52] bg-[#0f2747] p-3.5 text-base text-white outline-none"
          />
          <button
            onClick={handleStart}
            className="mt-3 w-full rounded-lg bg-[#d4af37] p-3.5 text-[17px] font-bold text-[#1a1205]"
          >
            Start scanning
          </button>
        </div>
      )}

      {/* Scanner */}
      {started && (
        <div className="mx-auto w-full max-w-lg p-4">
          <div
            id={REGION_ID}
            className="mx-auto w-full overflow-hidden rounded-2xl bg-black"
          />
          <p className="my-3 text-center text-sm text-slate-300">
            {camError
              ? "Camera unavailable — allow permission / use HTTPS, or type the code below."
              : "Point the camera at the ticket QR."}
          </p>
          <input
            type="text"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitCode(manual.trim())}
            placeholder="Or type the code manually"
            autoComplete="off"
            className="w-full rounded-lg border border-[#2b3a52] bg-[#0f2747] p-3.5 text-base text-white outline-none"
          />
          <button
            onClick={() => submitCode(manual.trim())}
            className="mt-3 w-full rounded-lg bg-[#20355a] p-3.5 text-[17px] font-bold text-white"
          >
            Check this code
          </button>
        </div>
      )}

      {/* Result overlay */}
      {result && (
        <div
          className={`fixed inset-0 z-50 flex flex-col items-center justify-center px-5 ${view.bg}`}
        >
          {/* Result card */}
          <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Status header */}
            <div className="flex flex-col items-center px-6 pt-8 pb-6 text-center">
              <div
                className={`mb-4 flex h-20 w-20 items-center justify-center rounded-full text-[40px] leading-none text-white ring-8 ring-black/5 ${view.bg}`}
              >
                {view.icon}
              </div>
              <h1 className="m-0 text-[22px] font-extrabold tracking-tight text-zinc-900">
                {view.title}
              </h1>
              {view.name && (
                <div className="mt-1 text-[17px] font-semibold text-zinc-700 uppercase">
                  {view.name}
                </div>
              )}
            </div>

            {/* Perforation divider (ticket stub) */}
            {(view.category || view.paymentId || view.price) && (
              <div className="relative flex items-center px-4" aria-hidden="true">
                <span className={`absolute -left-3 h-6 w-6 rounded-full ${view.bg}`} />
                <span className="w-full border-t-2 border-dashed border-zinc-200" />
                <span className={`absolute -right-3 h-6 w-6 rounded-full ${view.bg}`} />
              </div>
            )}

            {/* Details */}
            {(view.category || view.paymentId || view.price) && (
              <div className="space-y-3 px-6 py-6">
                
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400">
                      Category
                    </span>
                    <span className="flex items-center gap-1.5 text-right text-[14px] font-semibold text-zinc-800">
                      {/with-?out/i.test(view.category) ? (
                        <UtensilsCrossed className="h-4 w-4 shrink-0 text-red-500" />
                      ) : (
                        <Utensils className="h-4 w-4 shrink-0 text-emerald-600" />
                      )}
                      {view.category || "With-Food"}
                    </span>
                  </div>
                
                {view.paymentId && (
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400">
                      Payment ID
                    </span>
                    <span className="text-right font-mono text-[13px] font-medium text-zinc-800">
                      {view.paymentId}
                    </span>
                  </div>
                )}
                {view.price && (
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400">
                      Amount
                    </span>
                    <span className="text-right text-[14px] font-bold text-zinc-900">
                      {view.price}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Primary action */}
          <button
            onClick={resume}
            className="mt-5 w-full max-w-sm rounded-xl bg-white py-4 text-[16px] font-bold text-zinc-900 shadow-lg transition active:scale-[0.98]"
          >
            Scan next
          </button>
        </div>
      )}
    </div>
  );
}

function describeResult(result) {
  if (!result) return {};
  const ticket = result.ticket || {};
  console.log(ticket, 'tkt')
  if (result.ok) {
    return {
      bg: "bg-[#16a34a]",
      icon: "✓",
      title: "Welcome",
      name: ticket?.name || "",
      meta: ticket?.eventName || "",
      paymentId: ticket?.paymentId || "",
      category: ticket?.notes?.product || "",
      price: ticket?.notes?.amount || "",
    };
  }
  switch (result.reason) {
    case "already_used":
      return {
        bg: "bg-[#d97706]",
        icon: "!",
        title: "Already checked in",
        name: ticket.name || "",
        meta: result.at ? `Used at ${new Date(result.at).toLocaleString()}` : "",
        paymentId: ticket?.paymentId || "",
        category: ticket?.notes?.product || "",
        price: ticket?.notes?.amount || "",
      };
    case "unauthorized":
      return { bg: "bg-[#dc2626]", icon: "✕", title: "Wrong gate key", meta: "Reload and re-enter the key." };
    case "not_found":
      return { bg: "bg-[#dc2626]", icon: "✕", title: "Not a valid ticket", meta: "" };
    case "invalid_or_forged":
      return { bg: "bg-[#dc2626]", icon: "✕", title: "Invalid / forged QR", meta: "" };
    case "network_error":
      return { bg: "bg-[#dc2626]", icon: "✕", title: "Network error", meta: "Check connection and retry." };
    default:
      return { bg: "bg-[#dc2626]", icon: "✕", title: "Error", meta: result.reason || "Try again" };
  }
}